import type { UserPreferenceSource } from "@verbleif/lib/src/composables/Api/Type/UserPreferences/user_preferences.interface";
import { UserPreferencesTheme } from "@verbleif/lib/src/composables/Api/Type/UserPreferences/user_preferences.interface";
import { watch } from "vue";
import { useAuthStore } from "../AuthStore/useAuthStore";
import { useUserPreferences } from "../Users/UserPreferences/useUserPreferences";
import { ThemeSetting, useTheme } from "./Theme";

/**
 * The theme is a preference (Mijn account > Voorkeuren, "Geldt voor web en app, op al je apparaten"). The web keeps
 * the last choice in local storage so it can paint before the preferences load; this keeps both in step.
 */

export function themeSettingOf(theme: UserPreferencesTheme): ThemeSetting {
  switch (theme) {
    case UserPreferencesTheme.Light:
      return ThemeSetting.LIGHT;
    case UserPreferencesTheme.Dark:
      return ThemeSetting.DARK;
    case UserPreferencesTheme.System:
      return ThemeSetting.AUTO;
  }
  throw new Error(`Unknown theme preference ${String(theme)}.`);
}

export function themePreferenceOf(setting: ThemeSetting): UserPreferencesTheme {
  switch (setting) {
    case ThemeSetting.LIGHT:
      return UserPreferencesTheme.Light;
    case ThemeSetting.DARK:
      return UserPreferencesTheme.Dark;
    case ThemeSetting.AUTO:
      return UserPreferencesTheme.System;
  }
  throw new Error(`Unknown theme setting ${String(setting)}.`);
}

/**
 * Only a theme the person chose counts. It is stored on their row for all locations only; the Verbleif default and
 * the Verbleif-wide row leave the choice made on this device alone.
 */
export function isChosenThemeSource(source: UserPreferenceSource | undefined): boolean {
  return source === "user";
}

/**
 * Theme choices made on this device whose save still runs. While one runs, the preferences go back and forth: the
 * optimistic value, the stored value again when the save fails, and refetches that may have started before the save.
 * None of these may overrule the choice the person just made here.
 */
let choicesInFlight = 0;

/** Applies the theme preference to this device whenever the effective preferences change. */
export function useThemePreferenceSync(): void {
  const { userPreferences } = useAuthStore();
  const { themeSetting } = useTheme();

  watch(
    () => [userPreferences.value?.theme ?? null, userPreferences.value?.sources?.theme] as const,
    ([theme, source]) => {
      if (choicesInFlight > 0 || theme === null || !isChosenThemeSource(source)) {
        return;
      }
      themeSetting.value = themeSettingOf(theme);
    },
    // Synchronous, so a change made while a choice is saved is judged while that save still runs.
    { immediate: true, flush: "sync" },
  );
}

/**
 * Switches the theme on this device right away and stores it as the person's own preference. A failed save keeps
 * the switch on this device; the caller reports the error.
 */
export async function chooseTheme(setting: ThemeSetting): Promise<void> {
  const { themeSetting } = useTheme();
  const { upsertUserPreference } = useUserPreferences();
  choicesInFlight++;
  themeSetting.value = setting;
  try {
    await upsertUserPreference({ theme: themePreferenceOf(setting) });
  } finally {
    choicesInFlight--;
  }
}
